import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useRef, useState, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import Slider from "react-slick";
import { getAuth, signInWithCustomToken } from "firebase/auth/web-extension";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { doc, getFirestore, onSnapshot } from "firebase/firestore";

import "../assets/receive.scss";
import Text from "./text/Text";
import {
  accountFeesInfo,
  addAmount,
  addNote,
  address,
  amountTxt,
  btc,
  enterAmount,
  failedToGenerateQr,
  invoice,
  notes,
  reset,
  retry,
  sats,
  usdt,
  lnPaymentWarning,
  ethPaymentWarning,
  solPaymentWarning,
  trxPaymentWarning,
  tonPaymentWarning,
  transferSecureMsg,
  XAUT,
} from "./messages";
import {
  bitCoinSymbol,
  editIcon,
  editIconGrey,
  ethereumIcon,
  lightningIcons,
  solanaIcon,
  tronIcon,
  tonIcon,
  shieldLockIcon,
} from "./images";
import { QRCodeComponent } from "./qrCode/QRCodeComponent";
import {
  callAPIInterface,
  getFirebaseTokenForAccount,
  getExchangeRate,
  satsToBtcAmount,
  makeBoldString,
  renderReceiveHeader,
  getAccountFees,
  generateAmountToPass,
  defaultPaymentMethods,
  lightningLabel,
  ethereumLabel,
  tronLabel,
  showAmount,
  stableCoins,
  formatDisplayValues,
  solanaLabel,
  tonLabel,
  getSpeedFeeLabel,
} from "./constants";
import Dialog from "./dialog/Dialog";
import EnterAmountCommon from "./EnterAmountCommon";
import CommonSkeleton from "./CommonSkeleton";
import { app } from "../util/firebase";
import TruncatedTextTooltip from "./TruncatedTextTooltip";
import { useSession } from "../hooks/useSession";
import Clipboard from "./clipboard/Clipboard";
import WalletSymbols from "./WalletSymbols";
import QrInfoBox from "./QrInfoBox";

let unsubscribe;
const on_chain = "on_chain";
const tabLoaderCss = {
  borderRadius: "10px",
  width: "85%",
  height: "68px",
};
const warningNotes = {
  lightning: lnPaymentWarning,
  ethereum: ethPaymentWarning,
  tron: trxPaymentWarning,
  solana: solPaymentWarning,
  ton: tonPaymentWarning,
};

function ReceiveCommon({
  isReceiveModalOpen,
  setIsReceiveModalOpen,
  customClass,
  directionToOpen,
  qrSize,
  isFromBrowser,
}) {
  const sliderRef = useRef();
  const lightningRef = useRef();
  const { session } = useSession();
  const navigate = useNavigate();

  const { taprootAsset, walletCurrency, accountAssets, showBtcAmount } =
    useSelector((state) => state.common);
  const { uuid: currency } = { ...taprootAsset };
  const { code: targetedCurrency, symbol: targetedCurrencySymbol } = {
    ...walletCurrency,
  };

  const [selectedTab, setSelectedTab] = useState(1);
  const [note, setNote] = useState(null);
  const [isOpenEnterAmount, setIsOpenEnterAmount] = useState(false);
  const [amount, setAmount] = useState(null);
  const [convertedAmount, setConvertedAmount] = useState(null);
  const [qrLoader, setQrLoader] = useState(true);
  const [checkExchange, setCheckExchange] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(1);
  const [paymentId, setPaymentId] = useState(null);
  const [isContentChanged, setIsContentChanged] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [generatedPaymentMethodsArr, setGeneratedPaymentMethodArr] = useState(
    []
  );
  const [selectedMethodName, setSelectedMethodName] = useState(null);
  const [accountFees, setAccountFees] = useState(0);
  const [fixedFee, setFixedFee] = useState(0);
  const [isSelectedAmountOrNote, setIsSelectedAmountOrNote] =
    useState("amount");

  const showReset = !stableCoins.includes(taprootAsset?.uuid);
  const accountId = session?.account?.account?.id;
  const textSize = isFromBrowser ? 16 : 14;

  const isDisplayGeneratedAddress = !(
    selectedMethodName === lightningLabel ||
    (currency === usdt && selectedMethodName === on_chain)
  );

  const handleTabChange = (tab) => {
    sliderRef.current?.slickGoTo(tab === 1 ? 0 : tab - 1);
    setSelectedTab(tab);
  };

  const carouselProps = {
    dots: false,
    touchMove: true,
    infinite: false,
    arrows: false,
    onSwipe: (value) => {
      const arrLength = generatedPaymentMethodsArr?.length;

      let selectTab;
      if (value === "left") {
        if (arrLength === selectedTab) {
          selectTab = selectedTab;
        } else {
          selectTab = selectedTab + 1;
        }
      } else {
        if (selectedTab === 1) {
          selectTab = selectedTab;
        } else {
          selectTab = selectedTab - 1;
        }
      }

      const res = generatedPaymentMethodsArr?.find(
        (method) => method?.tab == selectTab
      );

      setSelectedMethodName(res?.name);
      setSelectedTab(selectTab);
    },
  };

  const handleExchangeRate = async () => {
    try {
      const res = await getExchangeRate(targetedCurrency, currency);
      setExchangeRate(res?.target_highest_rate);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    handleExchangeRate();
  }, []);

  useEffect(() => {
    const selectedCurrencyPaymentMethod = accountAssets?.find(
      (curr) => curr?.uuid === currency
    )?.payment_method_details;

    const sortedPaymentMethods = selectedCurrencyPaymentMethod?.sort(
      (a, b) => a?.weight - b?.weight
    );

    const paymentMethodArr = sortedPaymentMethods?.length
      ? selectedCurrencyPaymentMethod?.map(
          (payment) => payment?.payment_method_name
        )
      : defaultPaymentMethods[currency];

    setPaymentMethods(paymentMethodArr);
    setSelectedMethodName(paymentMethodArr[0]);
  }, [accountAssets]);

  const getGeneratedAddress = () => {
    return makeAddress(
      rowPaymentAddress?.newAddress,
      amount,
      selectedMethodName === on_chain ? "bitcoin" : selectedMethodName
    );
  };

  const displayQrFailedScreen = (
    <Box className="failedQr">
      <ErrorOutlineIcon
        sx={{
          height: "44px",
          width: "44px",
          color: "#D88A2C !important",
        }}
      />
      <Text size={20} font="semibold" className="default-text">
        {failedToGenerateQr}
      </Text>
      <Text
        size={12}
        font="regular"
        className="blue-text"
        sx={{ display: "flex", alignItems: "center" }}
        onClick={() => fetchPayment(amount, note)}
      >
        <WalletSymbols symbol="refresh" sx={{ color: "#2A67FF" }} />
        {retry}
      </Text>
    </Box>
  );

  const displayQR = () => {
    let paymentAddress, qrRef;
    paymentAddress = rowPaymentAddress?.newAddress;
    qrRef = lightningRef;
    const showWarning = currency !== sats;

    return qrLoader ? (
      <CommonSkeleton
        variant="rectangular"
        className="receive-qr-loader"
        sx={{
          borderRadius: "10px",
        }}
      />
    ) : rowPaymentAddress?.newAddress ? (
      <>
        <QRCodeComponent
          value={paymentAddress}
          size={qrSize}
          icon
          iconSize={25}
          level={paymentAddress?.length > 250 ? "L" : "H"}
          qrRef={qrRef}
          borderRadius="10px"
        />
        {showWarning && (
          <Text
            size={14}
            font="regular"
            className="grey-text"
            sx={{ margin: "10px 60px", textAlign: "center" }}
          >
            {warningNotes[selectedMethodName]?.(currency)}
          </Text>
        )}
        <Text
          size={14}
          font="regular"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "10px",
          }}
        >
          <img src={shieldLockIcon} height={20} width={20} />
          {transferSecureMsg}
        </Text>
      </>
    ) : (
      displayQrFailedScreen
    );
  };

  useEffect(() => {
    const zeroPaymentCurrency = [sats, XAUT];
    if (zeroPaymentCurrency.includes(currency)) {
      session && fetchPayment(0, "");
    } else {
      setIsOpenEnterAmount(true);
    }
  }, [session]);

  useEffect(() => {
    if (selectedMethodName) {
      fetchAccountFees();
    }
  }, [selectedMethodName]);

  const rowPaymentAddress = useMemo(() => {
    let newAddress;
    if (generatedPaymentMethodsArr?.length > 0) {
      newAddress = generatedPaymentMethodsArr?.find(
        (addr) => addr?.name === selectedMethodName
      )?.address;
    }

    return { newAddress };
  }, [paymentId, selectedTab, generatedPaymentMethodsArr]);

  const fetchAccountFees = async () => {
    try {
      const speedFee = await getAccountFees(
        selectedMethodName === on_chain ? "onchain" : selectedMethodName,
        currency,
        "01"
      );
      setAccountFees(speedFee.feePercentage);
      setFixedFee(speedFee.fixedFee);
    } catch (err) {}
  };

  const getPaymentData = async (paymentId, note) => {
    const db = getFirestore(app);
    const paymentDataRef = doc(db, "account", accountId, "payments", paymentId);
    try {
      unsubscribe = onSnapshot(paymentDataRef, async (querySnapshot) => {
        const data = querySnapshot.data();
        if (data) {
          if (data?.status?.toLowerCase() === "paid") {
            navigate("/success-page", {
              state: {
                details: data,
                type: "payment",
                target_currency: currency,
                note: note,
              },
            });
            unsubscribe();
          }
        }
      });
    } catch (error) {}
  };

  const makeAddress = (address, amount, addressHeader) => {
    const url = new URL(`${addressHeader}:${address}`);
    if (amount) {
      let amountToSendInAddress = 0;
      if (addressHeader === "bitcoin" && currency === sats) {
        amountToSendInAddress = showBtcAmount
          ? amount
          : satsToBtcAmount(amount);
      } else {
        amountToSendInAddress = amount;
      }
      url.searchParams.append("amount", amountToSendInAddress);
    }
    return url.toString();
  };

  const handleModalClose = () => {
    if (!isOpenEnterAmount) {
      setIsReceiveModalOpen(false);
      unsubscribe && unsubscribe();
    } else {
      setIsOpenEnterAmount(false);
      if (currency !== sats && !amount) {
        setIsReceiveModalOpen(false);
      }
    }
  };

  const getPaymentMethodIcon = (type) => {
    if (type === lightningLabel) {
      return lightningIcons[currency];
    } else if (type === on_chain) {
      return bitCoinSymbol;
    } else if (type === ethereumLabel) {
      return ethereumIcon;
    } else if (type === tronLabel) {
      return tronIcon;
    } else if (type === solanaLabel) {
      return solanaIcon;
    } else if (type === tonLabel) {
      return tonIcon;
    }
  };

  const generatePaymentMethodsObj = (methodObj) => {
    const replacedArray = paymentMethods?.map((item) =>
      item === "onchain" ? on_chain : item
    );

    const methodArr = replacedArray?.map((method, index) => {
      return {
        tab: index + 1,
        name: method,
        icon: getPaymentMethodIcon(method),
        address:
          methodObj[method]?.address || methodObj[method]?.payment_request,
      };
    });

    return methodArr;
  };

  const fetchPayment = (amount, note) => {
    const data = {
      amount: generateAmountToPass(amount, currency),
      currency: currency,
      ttl: 600,
      statement_descriptor: note?.substring(0, 100),
      target_currency: currency,
    };
    setQrLoader(true);
    callAPIInterface("POST", "/payments", data)
      .then(async (res) => {
        setQrLoader(false);
        setPaymentId(res?.id);

        const generatedArr = generatePaymentMethodsObj(
          res?.payment_method_options
        );
        setSelectedMethodName((prev) => {
          return prev || generatedArr[0]?.name;
        }); // selected first tab of payment method from the available payment method if previous payment method is not there (due to the error in USDT lightning)
        setGeneratedPaymentMethodArr(generatedArr);

        setIsOpenEnterAmount(false);

        const token = await getFirebaseTokenForAccount();
        const authObj = getAuth(app);
        token?.firebase_token &&
          signInWithCustomToken(authObj, token?.firebase_token)
            .then(() => {
              getPaymentData(res?.id, res?.statement_descriptor);
            })
            .catch((_error) => {});
      })
      .catch(() => {
        setQrLoader(false);
        handleModalClose();
      });
  };

  const handleSubmit = async (amount, note) => {
    isContentChanged ? fetchPayment(amount, note) : setIsOpenEnterAmount(false);
  };

  const renderTabData = (tabId, icon, text) => {
    return (
      <Box
        className={
          selectedTab === tabId ? `selected-tab` : "lightning-onchain-tab"
        }
        onClick={() => {
          handleTabChange(tabId);
          setSelectedMethodName(text);
        }}
        sx={{
          padding: "9px",
        }}
      >
        <Text
          size={textSize}
          font="semibold"
          className="default-class tab-content"
        >
          <img
            src={icon}
            height="24px"
            width="24px"
            alt="payment method icon"
          />
          {text === on_chain ? "bitcoin" : text}
        </Text>
      </Box>
    );
  };

  const renderLightningOnchainTabs = () => {
    return (
      <Box className="receive-tabs-box">
        {qrLoader ? (
          <>
            <CommonSkeleton
              {...tabLoaderCss}
              sx={{ transform: "scale(1, 1)", marginRight: "10px" }}
            />

            <CommonSkeleton
              {...tabLoaderCss}
              sx={{ transform: "scale(1, 1)" }}
            />
          </>
        ) : (
          <>
            {generatedPaymentMethodsArr?.map((method, index) => {
              return renderTabData(
                method?.tab,
                method?.icon,
                method?.name,
                index
              );
            })}
          </>
        )}
      </Box>
    );
  };

  const renderModalHeader = () => {
    if (isOpenEnterAmount) {
      return enterAmount;
    } else {
      return renderReceiveHeader(currency);
    }
  };

  const addAmountNote = (label, value) => {
    const isNoteEnable = !(
      value === "note" && selectedMethodName !== lightningLabel
    );
    return (
      <Text
        className={isNoteEnable ? "blue-text" : "grey-text"}
        font="regular"
        size={16}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          cursor: "pointer",
        }}
        onClick={() => {
          if (isNoteEnable) {
            setIsSelectedAmountOrNote(value);
            setIsOpenEnterAmount(true);
          }
        }}
      >
        {isNoteEnable ? (
          <>
            {label}
            <img src={editIcon} height={20} width={20} alt="edit icon" />
          </>
        ) : (
          "--"
        )}
      </Text>
    );
  };

  const calculatedAmount = useMemo(() => {
    let assetAmount = showAmount({
      amount: amount,
      currency: showBtcAmount ? btc : currency,
      showCommaSeparated: true,
      appendCurrency: true,
      multiplier: stableCoins.includes(currency) || currency === XAUT,
    });
    let fiatAmount = (
      <>
        {targetedCurrencySymbol}
        {showAmount({
          amount: convertedAmount,
          currency: currency,
          showCommaSeparated: true,
          multiplier: stableCoins.includes(currency) || currency === XAUT,
        })}
      </>
    );
    return { assetAmount, fiatAmount };
  }, [amount, convertedAmount, currency]);

  const feesAmount = useMemo(() => {
    if ((accountFees == null && fixedFee == null) || !amount) return "--";
    const baseCurrency = showBtcAmount ? btc : currency;
    const value = checkExchange ? convertedAmount : amount;

    const formattedValue =
      formatDisplayValues({
        amount: value,
        currency: baseCurrency,
        targetedCurrency: baseCurrency,
        multiplier:
          stableCoins.includes(currency) || checkExchange || currency === XAUT,
      })?.withoutComma || 0;

    let fixedFeeInMatchingUnit;
    if (checkExchange) {
      const fixedFeeInAsset = showAmount({
        amount: fixedFee,
        currency: currency,
      });

      fixedFeeInMatchingUnit = Number(fixedFeeInAsset) / exchangeRate;
    } else {
      fixedFeeInMatchingUnit = showAmount({
        amount: fixedFee,
        currency: currency,
        targetedCurrency: showBtcAmount && currency === sats ? btc : currency,
      });
    }

    const fee =
      (formattedValue * accountFees) / 100 + Number(fixedFeeInMatchingUnit);

    const formattedFee = showAmount({
      amount: fee,
      multiplier: 1,
      showCommaSeparated: true,
      currency: checkExchange ? currency : baseCurrency,
      appendCurrency: !checkExchange,
    });

    return checkExchange
      ? `${targetedCurrencySymbol}${formattedFee}`
      : formattedFee;
  }, [
    amount,
    convertedAmount,
    accountFees,
    checkExchange,
    showBtcAmount,
    currency,
    btc,
    stableCoins,
    targetedCurrencySymbol,
    exchangeRate,
    fixedFee,
  ]);

  const qrInfoArr = [
    {
      label: selectedMethodName === lightningLabel ? invoice : address,
      value: (
        <Box display="flex" alignItems="center">
          {qrLoader ? (
            <CommonSkeleton />
          ) : (
            <Clipboard
              text={
                isDisplayGeneratedAddress
                  ? getGeneratedAddress()
                  : rowPaymentAddress?.newAddress
              }
              height="18px"
            >
              <Text
                className="grey-text"
                font="regular"
                size={textSize}
                variant="subtitle1"
              >
                {makeBoldString(rowPaymentAddress?.newAddress)}
              </Text>
            </Clipboard>
          )}
        </Box>
      ),
    },
    {
      label: notes,
      value: note ? (
        <>
          {selectedMethodName === lightningLabel ? (
            <Box
              display="flex"
              alignItems="center"
              gap="2px"
              sx={{ cursor: "pointer" }}
            >
              <TruncatedTextTooltip
                textProps={{ className: "default-text" }}
                textValue={note}
                cellWidth="210px"
                fontSize={textSize}
              />
              <img
                src={editIconGrey}
                height={20}
                width={20}
                alt="edit icon"
                onClick={() => {
                  setIsSelectedAmountOrNote("note");
                  setIsOpenEnterAmount(true);
                }}
              />
            </Box>
          ) : (
            "--"
          )}
        </>
      ) : (
        addAmountNote(addNote, "note")
      ),
    },
    {
      label: amountTxt,
      value: amount ? (
        <Box display="flex" gap="4px">
          <Box display="flex" flexDirection="column" alignItems="end">
            <Text
              className="default-text"
              font="semibold"
              size={textSize}
              variant="subtitle1"
            >
              {checkExchange
                ? calculatedAmount?.fiatAmount
                : calculatedAmount?.assetAmount}
            </Text>
            <Text
              className="grey-text"
              font="regular"
              size={isFromBrowser ? 14 : 12}
            >
              {checkExchange
                ? calculatedAmount?.assetAmount
                : calculatedAmount?.fiatAmount}
            </Text>
          </Box>
          <img
            src={editIconGrey}
            alt="edit icon"
            height={20}
            width={20}
            style={{ cursor: "pointer" }}
            onClick={() => {
              setIsSelectedAmountOrNote("note");
              setIsOpenEnterAmount(true);
            }}
          />
        </Box>
      ) : (
        addAmountNote(addAmount, "amount")
      ),
    },
    {
      label: getSpeedFeeLabel({
        speedFeePercentage: accountFees,
        speedFixedFees: fixedFee,
      }),
      value: qrLoader ? (
        <CommonSkeleton />
      ) : (
        <Text className="default-text" font="regular" size={textSize}>
          {feesAmount}
        </Text>
      ),
      showInfo: true,
      infoMsg: accountFeesInfo,
    },
  ];

  const enterAmountCommonProps = {
    amount,
    setAmount,
    customClass,
    isFromBrowser,
    onSubmit: handleSubmit,
    convertedAmount,
    setConvertedAmount,
    note,
    setNote,
    setCheckExchange,
    exchangeRate,
    checkExchange,
    taprootAsset,
    setIsContentChanged,
    qrLoader,
    setQrLoader,
    setIsReset,
    isReset,
    selectedMethodName,
    isSelectedAmountOrNote,
    setIsSelectedAmountOrNote,
  };

  return (
    <Dialog
      isOpenModal={isReceiveModalOpen}
      setIsOpenModal={setIsReceiveModalOpen}
      modalHeader={renderModalHeader()}
      directionToOpen={directionToOpen}
      handleAction={() => handleModalClose()}
      isShowCloseIcon={isFromBrowser && !isOpenEnterAmount}
      additionalText={isOpenEnterAmount && showReset ? reset : ""}
      additionalTextColor="#2A67FF"
      additionalAction={() => setIsReset(true)}
    >
      {isOpenEnterAmount ? (
        <EnterAmountCommon {...enterAmountCommonProps} />
      ) : (
        <Box className={`${customClass}`}>
          <Box className="receive-wrapper">
            {renderLightningOnchainTabs()}
            <Box className={`onchain-lightning-qr`} marginBottom="10px">
              <Box
                sx={{
                  margin: qrLoader ? "10px 0 8px" : "0px",
                }}
              >
                <Slider {...carouselProps} ref={sliderRef}>
                  {qrLoader
                    ? displayQR()
                    : generatedPaymentMethodsArr?.map(() => displayQR())}
                </Slider>
              </Box>
            </Box>
            {rowPaymentAddress?.newAddress && (
              <QrInfoBox
                infoData={qrInfoArr}
                isFromBrowser={isFromBrowser}
                isFrom="receive"
                currency={currency}
              />
            )}
          </Box>
        </Box>
      )}
    </Dialog>
  );
}

export default ReceiveCommon;
